

import { Direction, Directionality } from '@angular/cdk/bidi';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Host,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewEncapsulation
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { zoomBadgeMotion } from 'mwc/core/animation';
import { MwcConfigKey, MwcConfigService, WithConfig } from 'mwc/core/config';
import { MwcNoAnimationDirective } from 'mwc/core/no-animation';
import { BooleanInput, MwcSafeAny, MwcSizeDSType } from 'mwc/core/types';
import { InputBoolean } from 'mwc/core/util';

import { badgePresetColors } from './preset-colors';
import { MwcBadgeStatusType } from './types';

const NZ_CONFIG_MODULE_NAME: MwcConfigKey = 'badge';

@Component({
  selector: 'mwc-badge',
  exportAs: 'mwcBadge',
  preserveWhitespaces: false,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [zoomBadgeMotion],
  template: `
    <ng-container *ngIf="mwcStatus || mwcColor">
      <span
        class="mwc-badge-status-dot mwc-badge-status-{{ mwcStatus || presetColor }}"
        [style.background]="!presetColor && mwcColor"
        [ngStyle]="mwcStyle"
      ></span>
      <span class="mwc-badge-status-text">
        <ng-container *mwcStringTemplateOutlet="mwcText">{{ mwcText }}</ng-container>
      </span>
    </ng-container>
    <ng-content></ng-content>
    <ng-container *mwcStringTemplateOutlet="mwcCount">
      <mwc-badge-sup
        *ngIf="showSup"
        [mwcOffset]="mwcOffset"
        [mwcSize]="mwcSize"
        [mwcTitle]="mwcTitle"
        [mwcStyle]="mwcStyle"
        [mwcDot]="mwcDot"
        [mwcOverflowCount]="mwcOverflowCount"
        [disableAnimation]="!!(mwcStandalone || mwcStatus || mwcColor || noAnimation?.mwcNoAnimation)"
        [mwcCount]="mwcCount"
        [noAnimation]="!!noAnimation?.mwcNoAnimation"
      ></mwc-badge-sup>
    </ng-container>
  `,
  host: {
    class: 'mwc-badge',
    '[class.mwc-badge-status]': 'mwcStatus',
    '[class.mwc-badge-not-a-wrapper]': '!!(mwcStandalone || mwcStatus || mwcColor)'
  }
})
export class MwcBadgeComponent implements OnChanges, OnDestroy, OnInit {
  readonly _mwcModuleName: MwcConfigKey = NZ_CONFIG_MODULE_NAME;
  static ngAcceptInputType_mwcShowZero: BooleanInput;
  static ngAcceptInputType_mwcShowDot: BooleanInput;
  static ngAcceptInputType_mwcDot: BooleanInput;
  static ngAcceptInputType_mwcStandalone: BooleanInput;
  showSup = false;
  presetColor: string | null = null;
  dir: Direction = 'ltr';
  private destroy$ = new Subject<void>();
  @Input() @InputBoolean() mwcShowZero: boolean = false;
  @Input() @InputBoolean() mwcShowDot = true;
  @Input() @InputBoolean() mwcStandalone = false;
  @Input() @InputBoolean() mwcDot = false;
  @Input() @WithConfig() mwcOverflowCount: number = 99;
  @Input() @WithConfig() mwcColor?: string = undefined;
  @Input() mwcStyle: { [key: string]: string } | null = null;
  @Input() mwcText?: string | TemplateRef<void> | null = null;
  @Input() mwcTitle?: string | null | undefined;
  @Input() mwcStatus?: MwcBadgeStatusType | string;
  @Input() mwcCount?: number | TemplateRef<MwcSafeAny>;
  @Input() mwcOffset?: [number, number];
  @Input() mwcSize: MwcSizeDSType = 'default';

  constructor(
    public mwcConfigService: MwcConfigService,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    @Optional() private directionality: Directionality,
    @Host() @Optional() public noAnimation?: MwcNoAnimationDirective
  ) {}
  ngOnInit(): void {
    this.directionality.change?.pipe(takeUntil(this.destroy$)).subscribe((direction: Direction) => {
      this.dir = direction;
      this.prepareBadgeForRtl();
      this.cdr.detectChanges();
    });
    this.dir = this.directionality.value;
    this.prepareBadgeForRtl();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { mwcColor, mwcShowDot, mwcDot, mwcCount, mwcShowZero } = changes;
    if (mwcColor) {
      this.presetColor = this.mwcColor && badgePresetColors.indexOf(this.mwcColor) !== -1 ? this.mwcColor : null;
    }
    if (mwcShowDot || mwcDot || mwcCount || mwcShowZero) {
      this.showSup = (this.mwcShowDot && this.mwcDot) || this.mwcCount! > 0 || (this.mwcCount! <= 0 && this.mwcShowZero);
    }
  }

  private prepareBadgeForRtl(): void {
    if (this.isRtlLayout) {
      this.renderer.addClass(this.elementRef.nativeElement, 'mwc-badge-rtl');
    } else {
      this.renderer.removeClass(this.elementRef.nativeElement, 'mwc-badge-rtl');
    }
  }

  get isRtlLayout(): boolean {
    return this.dir === 'rtl';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
